import { ArrowLeft, Minus, Plus, X } from "@phosphor-icons/react";
import { Sheet, SheetContent } from "@shopflo/ui/sheet";
import blockImage from "assests/images/block-image.png";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import Price from "components/common/Price";
import { useLocale } from "lib/hooks/useLocale";
import { useUpsellValues } from "lib/hooks/useUpsellValues";
import { UpsellPositionType, UpSellProductType } from "lib/types/upsell";
import { insertBeforeLastOccurrence, truncateString } from "lib/utils/helpers";
import React from "react";

const getVariantImageSrc = (imageLink?: string): string => {
  return insertBeforeLastOccurrence(imageLink ?? blockImage, ".jpg", "_small");
};

interface UpSellVariantSelectorDialogProps {
  upsellProduct?: UpSellProductType;
  isVariantSelectorOpen: boolean;
  closeVariantSelector: () => void;
  position: UpsellPositionType;
  onPreviousClick: () => void;
  onVariantSelected?: (variantId: string) => void;
  enbaleAddActions?: boolean;
  onSubmit?: (itemsData: Record<string, number>) => void;
  tabTitle?: string;
}

const UpSellVariantSelectorDialog: React.FC<UpSellVariantSelectorDialogProps> = ({
  upsellProduct,
  isVariantSelectorOpen,
  closeVariantSelector,
  position,
  onPreviousClick,
  onVariantSelected,
  enbaleAddActions = true,
  onSubmit,
  tabTitle
}) => {
  const { t } = useLocale();

  const {
    isLoading,
    itemsData,
    setItemsData,
    percentageDiscount,
    getCompareAtPrice,
    handleEditProduct,
    submitSelection,
  } = useUpsellValues({
    closeVariantSelector,
    upsellProduct: {
      product: upsellProduct!,
      position,
      tabTitle
    },
  });

  const handleClose = () => {
    setItemsData({});
    closeVariantSelector();
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(itemsData);
      return;
    }
    submitSelection();
  };

  const variants = upsellProduct?.variants || [];
  const productName = upsellProduct?.name || "";

  // Early return for better performance
  if (!upsellProduct || !variants.length) {
    return null;
  }

  return (
    <>
      <Sheet open={isVariantSelectorOpen} onOpenChange={closeVariantSelector}>
        <SheetContent withinContainer container={document.getElementById("checkout-ui-root")} side="bottom">
          <div className="flex flex-col max-h-[420px]">
            <Header productName={productName} onBack={onPreviousClick} onClose={handleClose} />

            <div className="flex-1 overflow-y-auto scrollbar-hide pt-4">
              <ul className="flex flex-col space-y-2 px-3" role="list">
                {variants.map((variant, index) => {
                  const itemQty = itemsData[variant?.variantId];
                  const isAddUnavailable =
                    variant?.inventoryPolicy === "DENY" && variant?.inventoryQuantity <= itemQty;
                  const isDiscounted =
                    Boolean(variant.isDiscountedUpsell) && Boolean(variant.discountedPrice);
                  const percDiscount = percentageDiscount(variant, isDiscounted);
                  const totalPrice = getCompareAtPrice(variant, isDiscounted);
                  const imageSrc = getVariantImageSrc(variant.imageLink);
                  const roundedDiscount = Math.round(percDiscount);

                  return (
                    <li
                      key={variant.variantId || `variant-${index}`}
                      className="flex w-full snap-start flex-col gap-3 py-2 cursor-pointer"
                      onClick={() => onVariantSelected?.(variant.variantId)}>
                      <div className="flex flex-row space-x-2">
                        <img
                          src={imageSrc}
                          alt={`${productName} - ${variant.name}`}
                          className="h-[5rem] min-h-[5rem] w-[5rem] min-w-[5rem] rounded-lg border border-gray-light object-cover"
                          loading="lazy"
                        />
                        <div className="flex w-full flex-col space-y-1">
                          <div className="flex flex-col space-y-1">
                            <p className="mt-1 text-sm font-medium text-coal-dark break-words">
                              {truncateString(productName, 40)}
                            </p>
                            <div className="flex flex-row space-x-1 text-xs font-medium text-coal-dark">
                              <span>{truncateString(variant?.name, 40)}</span>
                            </div>
                          </div>
                          <Price
                            total={isDiscounted ? variant.discountedPrice ?? 0 : variant.currentPrice}
                            compareAt={totalPrice}
                            orientation="horizontal"
                          />
                          {variant.isDiscountedUpsell && roundedDiscount > 0 && (
                            <span className="text-sm font-medium text-yay-dark">{roundedDiscount}% off</span>
                          )}
                        </div>
                        {enbaleAddActions && (
                          <div className="flex flex-col items-center justify-center">
                            {Boolean(itemQty) ? (
                              <div className="flex flex-row items-center justify-between space-x-1.5 rounded-md border-2 border-primary-dark px-2.5 py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProduct(variant?.variantId, itemQty - 1);
                                  }}>
                                  <Minus className="h-4 w-4" />
                                </button>
                                <label className="min-w-[1rem] text-center text-sm">{itemQty}</label>
                                <button
                                  disabled={isAddUnavailable}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProduct(variant?.variantId, itemQty + 1);
                                  }}
                                  className={`${isAddUnavailable ? "text-gray-light" : "text-coal-dark"}`}>
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="rounded-md bg-primary-dark px-4 py-1 text-btnPrimaryText"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditProduct(variant?.variantId, 1);
                                }}>
                                {t("add")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {enbaleAddActions && (
              <div className="p-4">
                <PrimaryButton
                  buttonText={t("confirm")}
                  id="flo_upsell_variant_select"
                  onClick={handleSubmit}
                  height="h-14"
                  isCheckout={false}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

// Header component for better organization (matching bottomsheet design)
interface HeaderProps {
  productName: string;
  onBack?: () => void;
  onClose: () => void;
}

const Header: React.FC<HeaderProps> = ({ productName, onBack, onClose }) => (
  <div className="flex w-full flex-row items-center justify-between py-3 px-4 border-b border-gray-light">
    <div className="flex flex-row items-center gap-2">
      <ArrowLeft
        className="h-6 w-6 cursor-pointer text-coal-dark focus:outline-none"
        onClick={onBack}
        role="button"
        tabIndex={0}
        aria-label="Go back"
      />
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium text-gray-dark">{truncateString(productName, 40)}</p>
        <h1 className="text-sm font-medium text-black">Choose Option</h1>
      </div>
    </div>
    <X
      className="h-6 w-6 cursor-pointer text-coal-dark focus:outline-none"
      onClick={onClose}
      role="button"
      tabIndex={0}
      aria-label="Close"
    />
  </div>
);

Header.displayName = "Header";

export default UpSellVariantSelectorDialog;
